/**
 * World Events Service — browser-side event emission.
 *
 * Emits trigger events (entity.opened, task.completed, etc.) to the
 * AgentSupervisor via the FastAPI REST bridge. No Y.Doc — events are
 * dispatched directly to tool-runtime via HTTP POST.
 *
 * For tool-runtime → browser pushes (ui commands, job progress), the browser
 * uses EventSource connections to tool-runtime's SSE endpoints directly.
 */

import { getCurrentDataspaceId, makeEvent } from './worldEvents';
import type { WorldEvent } from './worldEvents';
import { API_BASE_URL } from '../api/config';

// Module-level active thread tracker — updated live by SpaceChat, read by sidebar.
let _activeChatSpaceId = '';
let _activeChatThreadId = '';

export function setActiveChatThread(spaceId: string, threadId: string): void {
  _activeChatSpaceId = spaceId;
  _activeChatThreadId = threadId;
  console.log(`[WorldEvents] setActiveChatThread: space=...${spaceId.slice(-6)} thread=...${threadId.slice(-8)}`);
}

export function getActiveChatThread(spaceId: string): string {
  return _activeChatSpaceId === spaceId ? _activeChatThreadId : '';
}

/**
 * Emit a world event to the AgentSupervisor via the REST bridge.
 * All callers of this function emit Tier 1 (supervisor-trigger) events —
 * entity.opened, entity.created, integration.connected, message.sent, etc.
 * Fire-and-forget: failures are non-fatal.
 */
export function emitWorldEvent(event: WorldEvent): void {
  const dataspaceId = getCurrentDataspaceId();
  if (!dataspaceId) {
    console.warn('[WorldEvents] No dataspaceId — dropping event:', event.type);
    return;
  }

  const authToken = localStorage.getItem('authToken') || '';
  const userId = localStorage.getItem('userId') || '';

  fetch(`${API_BASE_URL}/world-events/emit`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
    body: JSON.stringify({ dataspaceId, event, userId }),
  }).catch(() => {}); // Non-fatal

  try {
    console.log(
      `[WorldEvents] emitted ${event.type} by ${event.actor.kind}:${event.actor.id.slice(-8)}` +
      (event.spaceId ? ` | space=...${event.spaceId.slice(-6)}` : '') +
      (Object.keys(event.data).length > 0 ? ` | data=${JSON.stringify(event.data).slice(0, 80)}` : ''),
    );
  } catch { /* non-fatal */ }
}

// ── Dev-mode debug helper ─────────────────────────────────────────────────────
// Usage from browser console:
//   window.__worldEvents.emit('integration.connected', { service: 'Gmail', serviceName: 'Gmail' })

if (typeof window !== 'undefined') {
  (window as any).__worldEvents = {
    emit(type: string, data: Record<string, any> = {}): void {
      const userId = localStorage.getItem('userId') || 'debug-user';
      const dataspaceId = getCurrentDataspaceId();
      const event = makeEvent(type as any, { kind: 'user', id: userId }, data, { dataspaceId });
      emitWorldEvent(event);
      console.info('[WorldEvents] 📤 Emitted:', event);
    },
  };
}
